import { faChevronUp } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    type SidebarBoxConfig,
    type MobileSidebarBox,
    type CombinedMobileSidebarBox,
    type SidebarBoxKey,
} from 'AdmissionsGuidance';
import Button from 'Button';
import { Drawer } from 'Drawer';
import { useEventLogger } from 'FrontRoyalAngular';
import { memo, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getMobileDrawerShouldHide, studentDashboardActions } from 'StudentDashboardSlice';
import useIsMobileMixin from 'StudentNetwork/react/useIsMobileMixin';
import { Text } from 'Text';
import { useMobileBoxContext } from '../MobileSidebar/MobileBoxContext';
import { isCombinedMobileSidebarBox } from '../StudentDashboardSidebarTypeGuards';

type Props = {
    heading: string;
    boxKey: SidebarBoxKey;
    ctaText?: string;
};

type Cta = Props & {
    onPress: () => void;
    buttonId: string;
};

type Expandable = Props & {
    expandedContent: React.ReactNode;
};

const isExpandable = (props: Props): props is Expandable => 'expandedContent' in props;
const isCta = (props: Props): props is Cta => 'onPress' in props;
const visibleOthers = (mobileBoxConfig: CombinedMobileSidebarBox): SidebarBoxConfig[] =>
    mobileBoxConfig.others.filter(other => !other.shouldHide && !!other.desktopContent);

function BasicMobileBoxComponent(props: Cta | Expandable) {
    const expandable = isExpandable(props);
    const EventLogger = useEventLogger();
    const { heading, ctaText } = props;
    const { xsOrSm } = useIsMobileMixin();
    const mobileDrawerShouldHide = useSelector(getMobileDrawerShouldHide);
    const dispatch = useDispatch();
    const mobileBoxConfig = useMobileBoxContext();

    const [isExpanded, setIsExpanded] = useState(false);

    const handlePress = useCallback(() => {
        if (expandable) {
            setIsExpanded(true);
        } else if (isCta(props)) {
            EventLogger.log('student_dashboard_sidebar_box:clicked_button', {
                type: `${props.boxKey}:${props.buttonId}`,
            });
            props.onPress();
        }
    }, [expandable, props, EventLogger]);

    useEffect(() => {
        if (!xsOrSm && isExpanded) {
            setIsExpanded(false);
        }
    }, [xsOrSm, isExpanded]);

    useEffect(() => {
        if (mobileDrawerShouldHide) {
            setIsExpanded(false);
            dispatch(studentDashboardActions.resetMobileDrawerShouldHide());
        }
    }, [mobileDrawerShouldHide, dispatch]);

    return (
        <BasicMobileBoxPresentation
            heading={heading}
            ctaText={ctaText}
            expandable={expandable}
            isExpanded={isExpanded}
            expandedContent={expandable ? props.expandedContent : undefined}
            handlePress={handlePress}
            onDrawerClose={() => setIsExpanded(false)}
            mobileBoxConfig={mobileBoxConfig}
        />
    );
}

export function BasicMobileBoxPresentation({
    heading,
    ctaText,
    expandable,
    isExpanded,
    expandedContent,
    handlePress,
    onDrawerClose,
    mobileBoxConfig,
}: {
    heading: string;
    ctaText?: string;
    expandable: boolean;
    isExpanded: boolean;
    expandedContent?: React.ReactNode;
    handlePress: () => void;
    onDrawerClose: () => void;
    mobileBoxConfig?: MobileSidebarBox;
}) {
    return (
        <>
            <div
                role="button"
                tabIndex={0}
                onKeyDown={e => {
                    if (e.key === 'Enter') {
                        handlePress();
                    }
                }}
                onClick={handlePress}
                className="flex h-full w-full items-center justify-between p-4"
            >
                <Text color="white" weight="semibold" className="leading-tight">
                    {heading}
                </Text>
                {!!ctaText && (
                    <Button
                        variant="bordered"
                        radius="full"
                        color="transparent"
                        height={6.25}
                        onPress={handlePress}
                        className="flex items-center justify-between gap-1"
                    >
                        {ctaText}
                        {expandable && <FontAwesomeIcon icon={faChevronUp} />}
                    </Button>
                )}
            </div>
            {expandable && (
                <Drawer
                    isOpen={isExpanded}
                    padding={0}
                    onClose={onDrawerClose}
                    bgColor="slate-grey-100-alt"
                    maxHeight="80vh"
                >
                    {/* Show the primary box content if a combined mobile box, or the sole box's content if not */}
                    {expandedContent}

                    {/* Show other boxes in the combined mobile box */}
                    {mobileBoxConfig &&
                        isCombinedMobileSidebarBox(mobileBoxConfig) &&
                        visibleOthers(mobileBoxConfig).length > 0 && (
                            <div className="mx-3.5 mt-7.5">
                                {visibleOthers(mobileBoxConfig).map(other => (
                                    <div key={other.key} className="mb-7.5">
                                        {/* within the drawer, we show the desktop version of the box */}
                                        {other.desktopContent}
                                    </div>
                                ))}
                            </div>
                        )}
                </Drawer>
            )}
        </>
    );
}

export const BasicMobileBox = memo(BasicMobileBoxComponent) as typeof BasicMobileBoxComponent;
